import { supabase, TENANT_ID } from '@/lib/supabase'
import type { B2BCustomer } from '@/types'
import type { QueryClient } from '@tanstack/react-query'

// New RPCs are isolated here until generated database types are refreshed.
export async function adminRpc<T>(name: string, args: Record<string, unknown>): Promise<T> {
  const rpc = supabase.rpc.bind(supabase) as unknown as (name: string, args: Record<string, unknown>) => PromiseLike<{ data: unknown; error: { message: string } | null }>
  const { data, error } = await rpc(name, args)
  if (error) throw new Error(error.message || 'Não foi possível salvar. Tente novamente.')
  return data as T
}

export function requestFor(key: string, payload: unknown): string {
  const fingerprint = JSON.stringify(payload)
  const saved = JSON.parse(sessionStorage.getItem(key) || 'null')
  const id = saved?.fingerprint === fingerprint ? saved.id : crypto.randomUUID()
  sessionStorage.setItem(key, JSON.stringify({ id, fingerprint }))
  return id
}

export async function saveAdminCustomer(customerId: string | null, data: Record<string, unknown>) {
  const payload = { p_tenant_id: TENANT_ID, p_customer_id: customerId, p_data: data }
  const key = `b2b-admin-customer-save:${customerId || 'new'}`
  const result = await adminRpc<B2BCustomer>('b2b_admin_save_customer', {
    ...payload, p_request_id: requestFor(key, payload),
  })
  if (!result?.id) throw new Error('Não foi possível confirmar o cadastro. Tente novamente para recuperá-lo.')
  sessionStorage.removeItem(key)
  return result
}

export async function invalidateAdminCustomer(cache: QueryClient) {
  cache.removeQueries({ queryKey: ['b2b-admin-customer-edit'], type: 'inactive' })
  await Promise.all(['b2b-admin-customers', 'b2b-admin-customer', 'b2b-admin-customer-edit', 'b2b-customer-price-lists',
    'b2b-customer-activity', 'b2b-admin-order-selected-customer', 'b2b-admin-order-customer-search', 'b2b-admin-order-lists',
    'b2b-admin-stats', 'b2b-operations'].map(key => cache.invalidateQueries({ queryKey: [key] })))
}

export function searchFilter(search: string): string {
  // Prevent PostgREST filter delimiters/wildcards from becoming operators.
  const term = search.replace(/[,().%_*\\"']/g, ' ').trim().slice(0, 100)
  if (!term) return ''
  const digits = term.replace(/\D/g, '')
  return [`razao_social.ilike.%${term}%`, `nome_fantasia.ilike.%${term}%`, `email.ilike.%${term}%`,
    ...(digits.length >= 3 ? [`cnpj.ilike.%${digits}%`, `cpf.ilike.%${digits}%`] : [])].join(',')
}

export function waitingLabel(date: string): string {
  const days = Math.max(0, Math.floor((Date.now() - new Date(date).getTime()) / 86400000))
  return days === 0 ? 'Hoje' : `Há ${days} ${days === 1 ? 'dia' : 'dias'}`
}
